// CalendarToolbar — period navigation (prev/next + label), a counts
// summary line, and the zoom toggle. The zoom segmented control is
// interactive across all three options (Month, Week, Day); the active
// button carries aria-pressed=true and the brand active treatment.
// Clicking a zoom option dispatches onZoomChange(value) — the URL/page
// owns the zoom state and re-renders with the new value.

import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ScheduleZoom } from "../../../hooks/useScheduleUrlState";

interface CalendarToolbarCounts {
  overdue: number;
  dueThisWeek: number;
  inView: number;
}

interface CalendarToolbarProps {
  periodLabel: string;
  counts: CalendarToolbarCounts;
  zoom?: ScheduleZoom;
  onZoomChange?: (next: ScheduleZoom) => void;
  onPrev: () => void;
  onNext: () => void;
}

const ZOOM_BASE =
  "px-3 py-1 text-body-sm font-medium rounded transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

const ZOOM_ACTIVE = "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100";
const ZOOM_INACTIVE =
  "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100";

const ZOOM_OPTIONS: { value: ScheduleZoom; label: string }[] = [
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
];

export default function CalendarToolbar({
  periodLabel,
  counts,
  zoom = "month",
  onZoomChange,
  onPrev,
  onNext,
}: CalendarToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Previous month"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50">
          {periodLabel}
        </h2>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next month"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <p
          data-testid="calendar-toolbar-counts"
          className="text-caption text-slate-600 dark:text-slate-400"
        >
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {counts.overdue}
          </span>{" "}
          overdue ·{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {counts.dueThisWeek}
          </span>{" "}
          due this week · {counts.inView} in view
        </p>

        <div
          role="group"
          aria-label="Calendar zoom"
          className="inline-flex gap-0 rounded-md bg-slate-100 p-0.5 dark:bg-slate-700"
        >
          {ZOOM_OPTIONS.map(({ value, label }) => {
            const active = zoom === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => onZoomChange?.(value)}
                className={`${ZOOM_BASE} ${active ? ZOOM_ACTIVE : ZOOM_INACTIVE}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
